import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  Onderneming,
  SpecificatiePriveGebruikAuto
} from '@snelstart-ventures/fiscaal/domain-logic';
import {EMPTY, filter, finalize, map, Observable, shareReplay, take, tap} from 'rxjs';
import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {SharedUiDialogModule} from "@snelstart/ui-dialog";

type AutoFormGroup = FormGroup<{
  autoWordtPriveGebruikt: FormControl<boolean | null>;
  kenteken: FormControl<string | null>;
  catalogusPrijs: FormControl<number | null>;
  periodePriveGebruik: PeriodeFormGroup;
  kilometerAdministratieAanwezig: FormControl<boolean | null>;
  kostenEnLastenVoorEigenRekening: FormControl<number | null>;
  priveGebruik: FormControl<number | null>;
}>;

type PeriodeFormGroup = FormGroup<{
  begindatum: FormControl<Date | null>;
  einddatum: FormControl<Date | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-prive-ontrekkingen-auto',
    templateUrl: './prive-ontrekkingen-auto.component.html',
    styleUrls: ['./prive-ontrekkingen-auto.component.scss'],
    imports: [SharedUiFormControlsModule, SharedUiButtonModule, SharedUiDetailsModule, CommonModule, SharedUiDialogModule],
})
export class PriveOntrekkingenAutoComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    public dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    public index = 0;
    public indexOnderneming = 0;
    public dialogVisible = false;
    public isOndernemer = false;
    public isPartner = false;
    public disabledCatalogusPrijs: boolean[] = [];

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' },
    ];

    public optionsJaNeeKms: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee/N.v.t' },
    ];

    public formGroup = new FormGroup({
        gebruikAuto: new FormArray<AutoFormGroup>([]),
    });

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly indexOnderneming$: Observable<number> = this.dataService.indexOnderneming$.pipe(
        tap((data) => {
            this.indexOnderneming = data;
        }),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    ngOnInit(): void {
        this.indexOnderneming$.subscribe();
        this.dataService.partnerAangifte$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((partner) => {
            this.isPartner = partner;
            this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((entity) => {
                if (partner) {
                    this.handleSwitch(entity, true);
                } else {
                    this.handleSwitch(entity, false);
                }
            });
        });

        // Subscribe to changes in FormArrays
        this.privegebruikAuto.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.saveForm());
    }

    ngOnDestroy(): void {
        this.dataService.calculateLatestIncomeTaxReturn();
    }

    private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
        const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
        if (details) {
            this.resetAndPatchForms(details);
        }
    }

    getCatalogusPrijs(i: number): void {
        const formControl = this.privegebruikAuto.at(i);
        const kenteken = formControl.controls.kenteken.value;

        if (!kenteken) {
            formControl.patchValue({ catalogusPrijs: null }, { emitEvent: false });
            this.disabledCatalogusPrijs[i] = false;
            this.saveForm();
            return;
        }

        if (!this.dutchKentekenValidator()) {
            this.disabledCatalogusPrijs[i] = false;
            return;
        }

        this.dataService
            .getCatalogusPrijs(kenteken.toUpperCase())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (catalogusPrijs) => {
                    formControl.patchValue({ catalogusPrijs }, { emitEvent: false });
                    this.disabledCatalogusPrijs[i] = catalogusPrijs !== null && catalogusPrijs !== undefined;
                    this.saveForm();
                },
                error: (error) => {
                    console.error('Fout bij ophalen catalogusprijs:', error);
                    formControl.patchValue({ catalogusPrijs: null }, { emitEvent: false });
                    this.disabledCatalogusPrijs[i] = false;
                    this.saveForm();
                },
            });
    }

    onAutoWordtPriveGebruiktChange(i: number): void {
        const formControl = this.privegebruikAuto.at(i);
        const priveGebruikControl = formControl.controls.priveGebruik;
        const autoWordtPriveGebruikt = formControl.controls.autoWordtPriveGebruikt.value;

        if (autoWordtPriveGebruikt === false) {
            priveGebruikControl.setValue(0, { emitEvent: false });
            priveGebruikControl.disable({ emitEvent: false });

            this.saveForm();
            return;
        }

        priveGebruikControl.enable({ emitEvent: false });

        this.index = i;
        this.calculateBijtelling(formControl)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                error: (error) => {
                    console.error('Fout bij berekenen bijtelling:', error);
                },
            });
    }

    private resetAndPatchForms(details: Aangifte): void {
        this.formGroup.reset({}, { emitEvent: false });
        this.isOndernemer = false;
        if (details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)) {
            this.isOndernemer = true;
            this.updateForm(details.box1?.winstUitOnderneming?.ondernemingen?.at(this.indexOnderneming)?.onttrekkingPriveGebruikAuto || []);
        }
    }

    showDialog(index: number): void {
        this.index = index;
        this.dialogVisible = true;
    }

    get privegebruikAuto(): FormArray<AutoFormGroup> {
        return this.formGroup.controls.gebruikAuto;
    }

    addRijAuto(): void {
        this.privegebruikAuto.push(this.createFormGroup(), { emitEvent: false });
    }

    verwijderRijAuto(index: number): void {
        this.privegebruikAuto.removeAt(index, { emitEvent: false });
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        this.formGroup.markAsDirty({ emitEvent: false });
        this.saveForm();
    }

    createFormGroup(item?: SpecificatiePriveGebruikAuto): FormGroup {
        const formGroup = new FormGroup({
            autoWordtPriveGebruikt: new FormControl<boolean | null>(item?.autoWordtPriveGebruikt ?? null, Validators.required),
            kenteken: new FormControl<string | null>(item?.kenteken ?? null, [Validators.required]),
            catalogusPrijs: new FormControl<number | null>(item?.catalogusPrijs ?? null),
            periodePriveGebruik: new FormGroup({
                begindatum: new FormControl<Date | null>(item?.periodePriveGebruik?.begindatum ?? null, Validators.required),
                einddatum: new FormControl<Date | null>(item?.periodePriveGebruik?.einddatum ?? null, Validators.required),
            }),
            kilometerAdministratieAanwezig: new FormControl<boolean | null>(item?.kilometerAdministratieAanwezig ?? false),
            kostenEnLastenVoorEigenRekening: new FormControl<number | null>(item?.kostenEnLastenVoorEigenRekening ?? null),
            priveGebruik: new FormControl<number | null>(item?.priveGebruik ?? 0),
        });
        if ((item?.autoWordtPriveGebruikt ?? null) === false) {
            formGroup.controls.priveGebruik.disable({ emitEvent: false });
        }

        return formGroup;
    }

    private updateForm(loon: SpecificatiePriveGebruikAuto[]): void {
        let lijst = loon || [];
        const formArray = this.privegebruikAuto;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1, { emitEvent: false });
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(lijst[i], { emitEvent: false });
            } else {
                const formGroup = this.createFormGroup(lijst[i]);
                formArray.push(formGroup, { emitEvent: false });
            }
        }
    }

    saveForm(): void {
        const idx = this.indexOnderneming;
        const formValue = this.formGroup.getRawValue();
        const gebruikAuto = (formValue.gebruikAuto ?? []).map((auto) => ({
            ...auto,
            periodePriveGebruik: auto.periodePriveGebruik
                ? {
                      begindatum: this.toApiDate(auto.periodePriveGebruik.begindatum),
                      einddatum: this.toApiDate(auto.periodePriveGebruik.einddatum),
                  }
                : undefined,
        }));

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }
            const update = (aangifte: any) => {
                const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
                const onderneming = existingOndernemingen[idx] || {};
                const updatedOnderneming: Partial<Onderneming> = {
                    ...onderneming,
                    onttrekkingPriveGebruikAuto: gebruikAuto,
                };
                // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
                const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
                const updatedOndernemingen = existingOndernemingen.map((o: Onderneming, i: number) => (i === idx ? cleanedOnderneming : o));
                return {
                    ...aangifte,
                    box1: {
                        ...aangifte.box1,
                        winstUitOnderneming: {
                            ...aangifte.box1?.winstUitOnderneming,
                            ondernemingen: updatedOndernemingen,
                        },
                    },
                };
            };
            const updatedAangifte = this.isPartner
                ? {
                      ...currentEntity.aangifte,
                      partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
                  }
                : update(currentEntity.aangifte);
            return this.dataService.deepClean({
                ...currentEntity,
                aangifte: updatedAangifte,
            }) as IncomeTaxEntity;
        });
    }

    private toApiDate(value: Date | string | null | undefined): string | undefined {
        if (!value) {
            return undefined;
        }

        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) {
            return undefined;
        }

        return date.toISOString().split('T')[0];
    }

    onCalculateRequest(): void {
        const formControl = this.privegebruikAuto.at(this.index) as FormGroup;
        this.calculateBijtelling(formControl)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                error: (error) => {
                    console.error('Fout bij ophalen van gegevens:', error);
                },
            });
    }

    calculateBijtelling(formControl: AutoFormGroup): Observable<void> {
        const kentekenControl = formControl.controls.kenteken.value ?? '';
        const periodePriveGebruik = formControl.controls.periodePriveGebruik.value;
        const beginDatumProcessed = new Date(periodePriveGebruik?.begindatum ?? new Date()).toISOString();
        const eindDatumProcessed = new Date(periodePriveGebruik?.einddatum ?? new Date()).toISOString();
        const privebetaald = formControl.controls.kostenEnLastenVoorEigenRekening.value ?? 0;
        return this.dataService
            .getKentekenGegevens(kentekenControl.toUpperCase(), privebetaald, beginDatumProcessed, eindDatumProcessed)
            .pipe(
                tap((response) => {
                    if (response.length > 0 && response[0]?.catalogusprijs) {
                        const catalogusPrijs = Number(response[0].catalogusprijs);
                        const priveGebruik = Number(response[0].bijtelling);
                        formControl.patchValue({ catalogusPrijs }, { emitEvent: false });
                        formControl.controls.priveGebruik.setValue(priveGebruik, { emitEvent: false });
                        this.saveForm();
                    } else {
                        console.error('Kenteken niet gevonden.');
                    }
                }),
                map(() => void 0),
                finalize(() => {
                    this.dialogVisible = false;
                }),
            );
    }

    dutchKentekenValidator(): boolean {
        const value = this.privegebruikAuto.at(this.index).value.kenteken;
        const allowedLetters = '[B-DF-HJ-NP-TV-Z]';
        const dutchKentekenPatterns = [
            new RegExp(`^${allowedLetters}{2}\\d{2}\\d{2}$`),
            new RegExp(`^\\d{2}\\d{2}${allowedLetters}{2}$`),
            new RegExp(`^\\d{2}${allowedLetters}{2}\\d{2}$`),
            new RegExp(`^${allowedLetters}{2}\\d{2}${allowedLetters}{2}$`),
            new RegExp(`^${allowedLetters}{2}${allowedLetters}{2}\\d{2}$`),
            new RegExp(`^\\d{2}${allowedLetters}{2}${allowedLetters}{2}$`),
            new RegExp(`^\\d{2}${allowedLetters}{3}\\d$`),
            new RegExp(`^\\d${allowedLetters}{3}\\d{2}$`),
            new RegExp(`^${allowedLetters}{2}\\d{3}${allowedLetters}$`),
            new RegExp(`^${allowedLetters}\\d{3}${allowedLetters}{2}$`),
            new RegExp(`^${allowedLetters}{3}\\d{2}${allowedLetters}$`),
            new RegExp(`^${allowedLetters}\\d{2}${allowedLetters}{3}$`),
        ];

        if (!value) {
            return true;
        }

        const normalized = String(value).toUpperCase().replace(/[-\s]/g, '');
        return dutchKentekenPatterns.some((pattern) => pattern.test(normalized));
    }
}
